import React from 'react';
import Button from '../ui/Button';
import SectionLabel from '../ui/SectionLabel';
import './Hero.css';

const pillars = [
  {
    num: '01',
    label: 'AI Research & Innovation',
    serviceNum: '01',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a7 7 0 0 1 7 7c0 4-3 6-3 9H8c0-3-3-5-3-9a7 7 0 0 1 7-7z"/>
        <line x1="9" y1="21" x2="15" y2="21"/>
        <line x1="9.7" y1="17" x2="14.3" y2="17"/>
      </svg>
    ),
  },
  {
    num: '02',
    label: 'Healthcare · Finance · Logistics',
    serviceNum: '02',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <path d="M8 21h8M12 17v4"/>
      </svg>
    ),
  },
  {
    num: '03',
    label: 'Ethical & Responsible AI',
    serviceNum: '03',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    num: '04',
    label: 'AI Security',
    serviceNum: '06',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
  {
    num: '05',
    label: 'Partnerships & Talent',
    serviceNum: '04',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
];

const scrollToService = (serviceNum: string) => {
  const card = document.querySelector<HTMLElement>(
    `.services__card[data-num="${serviceNum}"]`
  );
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.add('services__card--highlight');
    setTimeout(() => card.classList.remove('services__card--highlight'), 1200);
  }
};

const Hero: React.FC = () => {
  return (
    <section className="hero" id="home">

      <div className="hero__grid" />
      <svg className="hero__bg-svg" viewBox="0 0 900 600" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="diagHero" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="28" stroke="#1A50C8" strokeWidth="0.5" opacity="0.07"/>
          </pattern>
        </defs>
        <rect width="900" height="600" fill="url(#diagHero)"/>
        <text x="420" y="480" fontSize="320" fontWeight="900"
          fill="#1A50C8" opacity="0.03" letterSpacing="-10" fontFamily="sans-serif">AI</text>
        <polyline points="820,20 870,20 870,70" fill="none" stroke="#1A50C8" strokeWidth="1.2" opacity="0.15"/>
        <polyline points="20,530 20,580 70,580" fill="none" stroke="#1A50C8" strokeWidth="1.2" opacity="0.15"/>
      </svg>

      <div className="hero__bottom-bar" />

      <div className="hero__content">

        {/* ── Left ── */}
        <div className="hero__left">

          <div className="hero__top">
            <SectionLabel text="Rapidus AI Private Limited · Est. 2026" />
          </div>

          <div className="hero__bottom">
            <div className="hero__text-block">
              <div className="hero__line hero__line--intro">AI That Moves At</div>
              <div className="hero__headline-row">
                <span className="hero__big-word">the Speed</span>
                <span className="hero__italic-word">of Ambition</span>
                <span className="hero__badge">AI · CV · ML</span>
              </div>
              <div className="hero__line hero__line--sub-heading">
                Intelligent &amp; <span className="hero__blue-bold">Scalable</span> AI Solutions
              </div>
            </div>

            <p className="hero__sub">
              Production-ready AI systems that compress timelines and unlock
              new possibilities across healthcare, finance, logistics, and beyond.
            </p>

            <div className="hero__bottom-row">
              <div className="hero__actions">
                <Button variant="primary" href="#services">Explore Services</Button>
                <Button variant="ghost" href="#projects">View Work →</Button>
              </div>
            </div>
          </div>

        </div>

        {/* ── Vertical divider ── */}
        <div className="hero__divider" />

        {/* ── Right ── */}
        <div className="hero__visual">
          <span className="hero__pillars-label">What We Do</span>
          <div className="hero__pillars">
            {pillars.map((p, i) => (
              <div
                className="hero__pillar"
                key={p.num}
                style={{ animationDelay: `${0.65 + i * 0.08}s` }}
                onClick={() => scrollToService(p.serviceNum)}
                onKeyDown={(e) => e.key === 'Enter' && scrollToService(p.serviceNum)}
                role="button"
                tabIndex={0}
                aria-label={`${p.label} — scroll to service`}
              >
                <span className="hero__pillar-num">{p.num}</span>
                <div className="hero__pillar-icon">{p.icon}</div>
                <span className="hero__pillar-text">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

    </section>
  );
};

export default Hero;